/**
 * Monthly Calendar Formatter
 *
 * Formats calendar events for monthly view display.
 * Handles date/time formatting, event transformation, and grouping.
 *
 * Follows single responsibility principle: only transforms data for UI display.
 */

import type { CalendarEvent } from '@/types/events';
import type { MarkedDatesObject, RNCalendarsEvent } from '@/types/monthly-calendar';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import {
  MonthlyCalendarColorEnricher,
  type ColorResolutionContext,
} from '@/lib/calendar/MonthlyCalendarColorEnricher';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';

/**
 * MonthlyCalendarFormatter
 *
 * Static utility class for formatting events for monthly calendar view.
 */
export class MonthlyCalendarFormatter {
  /**
   * Build markedDates object for react-native-calendars
   * Keys are date strings (YYYY-MM-DD), values contain events array
   * @param colorContext - Optional context for resolving family member colors from mappings
   */
  static buildMarkedDatesObject(
    events: CalendarEvent[],
    selectedDate: string,
    todayDate: string,
    colorContext?: ColorResolutionContext
  ): MarkedDatesObject {
    const dateEventMap = this.buildDateEventMap(events);
    const markedDates: MarkedDatesObject = {};

    // Add events for each date
    dateEventMap.forEach((dayEvents, dateKey) => {
      markedDates[dateKey] = {
        events: dayEvents.map(event => this.toRNCalendarsEvent(event, colorContext)),
      };
    });

    // Mark selected date
    if (markedDates[selectedDate]) {
      markedDates[selectedDate].selected = true;
    } else {
      markedDates[selectedDate] = { selected: true };
    }

    // Mark today
    if (markedDates[todayDate]) {
      markedDates[todayDate].today = true;
    } else {
      markedDates[todayDate] = { today: true };
    }

    return markedDates;
  }

  /**
   * Convert CalendarEvent to RNCalendarsEvent format for custom day rendering
   * @param colorContext - Optional context for resolving family member colors from mappings
   */
  static toRNCalendarsEvent(
    event: CalendarEvent,
    colorContext?: ColorResolutionContext
  ): RNCalendarsEvent {
    const familyMemberColor = colorContext
      ? MonthlyCalendarColorEnricher.resolveFamilyMemberColor(event, colorContext)
      : this.getFamilyMemberColor(event);

    const taskCount = TaskCountResolver.resolve(event.description);

    return {
      id: event.id,
      title: event.title,
      color: this.getEventColor(event),
      isAllDay: event.allDay ?? false,
      familyMemberColor,
      openTaskCount: taskCount?.openCount,
      totalTaskCount: taskCount?.totalCount,
    };
  }

  /**
   * Extract family member color from event participants
   * Looks for participant with role 'label' which represents family member assignments
   */
  private static getFamilyMemberColor(event: CalendarEvent): string | undefined {
    const participants = event.extendedProperties?.participants;
    if (!participants || participants.length === 0) {
      return undefined;
    }

    const labelParticipant = participants.find(p => p.role === 'label');
    return labelParticipant?.familyMemberColor;
  }

  /**
   * Build a map of date strings to arrays of events for that date
   * Used for efficient lookup of events by date
   */
  static buildDateEventMap(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
    const map = new Map<string, CalendarEvent[]>();

    events.forEach(event => {
      // Add event to its start date
      if (!map.has(event.startDate)) {
        map.set(event.startDate, []);
      }
      map.get(event.startDate)!.push(event);

      // Handle multi-day events
      if (event.endDate && event.endDate !== event.startDate) {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);

        // Add event to each day it spans
        const currentDate = new Date(startDate);
        currentDate.setDate(currentDate.getDate() + 1);

        while (currentDate < endDate) {
          const dateKey = this.toDateKey(currentDate);
          if (!map.has(dateKey)) {
            map.set(dateKey, []);
          }
          map.get(dateKey)!.push(event);
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    // Sort events within each day
    map.forEach((dayEvents) => {
      dayEvents.sort((a, b) => {
        // All-day events first
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;

        // Then by start time
        const aTime = a.startTime || '00:00';
        const bTime = b.startTime || '00:00';
        return aTime.localeCompare(bTime);
      });
    });

    return map;
  }

  /**
   * Format event list header for selected day
   * Example: "Wednesday, November 1st 2025"
   */
  static formatEventListHeader(date: string): string {
    const parsed = new Date(date);

    const weekday = parsed.toLocaleDateString('en-US', { weekday: 'long' });
    const month = parsed.toLocaleDateString('en-US', { month: 'long' });
    const day = parsed.getDate();
    const ordinalSuffix = this.getOrdinalSuffix(day);
    const year = parsed.getFullYear();

    return `${weekday}, ${month} ${day}${ordinalSuffix} ${year}`;
  }

  /**
   * Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th, etc.)
   */
  private static getOrdinalSuffix(day: number): string {
    if (day >= 11 && day <= 13) return 'th';

    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /**
   * Truncate event title for text bar display
   */
  static truncateEventTitle(title: string, maxLength: number = 15): string {
    if (title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength - 3) + '...';
  }

  /**
   * Get color for event based on calendar or category
   */
  private static getEventColor(event: CalendarEvent): string {
    return '#6B9BD1';
  }

  /**
   * Convert Date object to YYYY-MM-DD string
   */
  static toDateKey(date: Date): string {
    return LocalDateFormatter.toDateKey(date);
  }
}
